<template>
  <v-card>
    <v-dialog v-model="showAddUser">
      <v-card>
        <v-card-title>Add a User</v-card-title>
        <v-card-text>
          <v-text-field
            label="Email"
            v-model="newUser.email"
          />
          <v-text-field
            label="Password"
            v-model="newUser.password"
            type="password"
          />
          <v-text-field
            label="First Name"
            v-model="newUser.firstName"
            type="text"
          />
          <v-text-field
            label="Last Name"
            v-model="newUser.lastName"
            type="text"
          />
          <v-select
            label="Role"
            v-model="newUser.role"
            :items="roles"
            item-text="name"
            item-value="id"
          />
          <div v-if="newUser.role !== undefined && newUser.role !== null" class="mt-2">
            <div v-if="accessiblePages(newUser).all" class="caption">
              <v-icon color="red" small>mdi-alert</v-icon>
              Read/write access to all pages.
            </div>
            <template v-else>
              <div class="mb-2">
                <span class="caption mr-2">Write:</span>
                <template v-if="accessiblePages(newUser).write.length">
                  <v-chip
                    v-for="path in accessiblePages(newUser).write"
                    :key="`nw-${path}`"
                    class="ma-1" small label
                  >{{ path }}</v-chip>
                </template>
                <span v-else class="caption font-italic">none</span>
              </div>
              <div>
                <span class="caption mr-2">Read:</span>
                <template v-if="accessiblePages(newUser).read.length">
                  <v-chip
                    v-for="path in accessiblePages(newUser).read"
                    :key="`nr-${path}`"
                    class="ma-1" small label outlined
                  >{{ path }}</v-chip>
                </template>
                <span v-else class="caption font-italic">none</span>
              </div>
            </template>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="showAddUser=false">Cancel</v-btn>
          <v-spacer/>
          <v-btn :loading="addingUser" @click="addNewUser(newUser)">Add User</v-btn>
        </v-card-actions>

      </v-card>

    </v-dialog>
    <v-dialog v-model="showPassword" max-width="400">
      <v-card>
        <v-card-title>Change Password</v-card-title>
        <v-card-text>
          <div class="mb-2">{{ passwordUser && passwordUser.email }}</div>
          <v-text-field
            label="New Password"
            v-model="newPassword"
            type="password"
            autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-btn @click="showPassword=false">Cancel</v-btn>
          <v-spacer/>
          <v-btn :loading="savingPassword" :disabled="!newPassword" @click="savePassword">Save Password</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card-title>Users
      <v-spacer/>
      <v-btn outlined @click="showAddUser=true">Add User</v-btn>
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-spacer/>
        <v-checkbox v-model="showRestricted" label="Show Restricted Users" class="mr-4"/>
        <v-checkbox v-model="showDeleted" label="Show Deleted Users"/>
      </v-row>
      <v-data-table
        :items="filteredUsers"
        :headers="headers"
        disable-pagination
        hide-default-footer
        sort-by="first_name"
        show-expand
        item-key="id"
        :expanded.sync="expanded"
      >
        <template v-slot:item.email="{item}">
          <a :href="`mailto:${item.email}`">{{ item.email }}</a>
          <v-icon
            v-if="accessiblePages(item).all"
            color="red" small class="ml-1"
            title="Read/write access to all pages"
          >mdi-alert</v-icon>
        </template>
        <template v-slot:item.role="{item}">
          <v-select
            :disabled="readOnly"
            class="ma-0 pa-0"
            solo flat
            v-model="item.editableRoles"
            :items="roles"
            item-text="name"
            item-value="id"
            multiple
            chips
            small-chips
            @change="saveRole(item)"
            hide-details
          />
        </template>
        <template v-slot:item.actions="{item}">
          <v-btn
            v-if="!readOnly"
            icon @click="openPasswordDialog(item)">
            <v-icon>mdi-key</v-icon>
          </v-btn>
          <!-- TODO: need to implement deleteUser method -->
          <v-btn
            v-if="!readOnly"
            icon @click="deleteUser(item)">
            <v-icon>mdi-trash-can</v-icon>
          </v-btn>
        </template>
        <template v-slot:expanded-item="{headers: cols, item}">
          <td :colspan="cols.length" class="py-3">
            <div v-if="accessiblePages(item).all" class="caption">
              <v-icon color="red" small>mdi-alert</v-icon>
              Read/write access to all pages.
            </div>
            <template v-else>
              <div class="mb-2">
                <span class="caption mr-2">Write:</span>
                <template v-if="accessiblePages(item).write.length">
                  <v-chip
                    v-for="path in accessiblePages(item).write"
                    :key="`w-${path}`"
                    class="ma-1" small label
                  >{{ path }}</v-chip>
                </template>
                <span v-else class="caption font-italic">none</span>
              </div>
              <div>
                <span class="caption mr-2">Read:</span>
                <template v-if="accessiblePages(item).read.length">
                  <v-chip
                    v-for="path in accessiblePages(item).read"
                    :key="`r-${path}`"
                    class="ma-1" small label outlined
                  >{{ path }}</v-chip>
                </template>
                <span v-else class="caption font-italic">none</span>
              </div>
            </template>
          </td>
        </template>

      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import {mapGetters} from 'vuex';
import api from '@/api';

export default {
  name: "Users",
  data() {
    return {
      users: [],
      roles: [],
      routes: [],
      expanded: [],
      headers: 'id,first_name,last_name,email,role,actions'
        .split(',')
        .map(p => ({value: p, text: p})),
      showDeleted: false,
      showRestricted: false,
      showPassword: false,
      passwordUser: null,
      newPassword: '',
      savingPassword: false,
      readOnly: false,
      showAddUser: null,
      newUser: {},
      addingUser: null
    }
  },
  created() {
    this.readOnly = !!this.$route.meta.readOnly;
  },
  mounted() {
    this.$router.options.routes.forEach(route => {
      if (route.meta && route.meta.requiresAuth) {
        this.routes.push({name: route.name, path: route.path});
      }
    });
    this.routes.sort((a, b) => a.path.localeCompare(b.path));
    api.get('users/roles')
      .then(response => {
        this.roles = response.data.sort((a, b) => a.name.localeCompare(b.name));
      })
    this.fetchUsers();
  },
  computed: {
    ...mapGetters([
      'getCurrentUser'
    ]),
    user() {
      console.log('user ', this.getCurrentUser);
      return this.getCurrentUser;
    },
    deletedRoleId() {
      const role = this.roles.find(r => r.name === 'deleted');
      return role && role.id;
    },
    restrictedRoleId() {
      const role = this.roles.find(r => r.name === 'restricted');
      return role && role.id;
    },
    filteredUsers() {
      const hasRole = (u, roleId) => {
        if (u.roles && u.roles.length > 0) return u.roles.includes(roleId);
        return u.role === roleId;
      };
      const hasDeletedRole = u => hasRole(u, this.deletedRoleId);
      const hasRestrictedRole = u => hasRole(u, this.restrictedRoleId);
      if (this.showDeleted) {
        return this.users.filter(hasDeletedRole);
      }
      return this.users.filter(u => {
        if (hasDeletedRole(u)) return false;
        if (!this.showRestricted && hasRestrictedRole(u)) return false;
        return true;
      });
    },
  },
  methods: {
    // pages a user can reach, aggregated across their assigned roles.
    // mirrors Roles.vue semantics: a role with no page restrictions grants
    // read/write everywhere, and write access implies read access.
    accessiblePages(user) {
      const roleIds = (user.editableRoles && user.editableRoles.length)
        ? user.editableRoles
        : (user.roles && user.roles.length
          ? user.roles
          : (user.role !== undefined && user.role !== null ? [user.role] : []));
      const roleRecords = roleIds
        .map(id => this.roles.find(r => r.id === id))
        .filter(Boolean);
      const readNames = new Set();
      const writeNames = new Set();
      let all = false;
      roleRecords.forEach(r => {
        const rp = r.readPages || [];
        const wp = r.writePages || [];
        if (rp.length === 0 && wp.length === 0) {
          all = true;
          return;
        }
        wp.forEach(n => { writeNames.add(n); readNames.add(n); });
        rp.forEach(n => readNames.add(n));
      });
      const toPath = name => {
        const route = this.routes.find(rt => rt.name === name);
        return (route && route.path) || name;
      };
      const sortPaths = names => names
        .map(toPath)
        .sort((a, b) => a.localeCompare(b));
      return {
        all,
        write: sortPaths(Array.from(writeNames)),
        // read-only pages: readable but not writable (write already shown above)
        read: sortPaths(Array.from(readNames).filter(n => !writeNames.has(n)))
      };
    },
    saveRole(user) {
      console.log('saving', JSON.stringify(user));
      api.post('users/role', {
        user: user.id,
        roles: user.editableRoles || []
      })
    },
    async addNewUser(user) {
      this.addingUser = true;
      user.userName = user.userName || user.email;
      await api.post('users/register', user)
        .then(r => console.log('result', r))
        .catch(e => {
          console.warn('failed', e);
          this.addingUser = false;
        })
        .then(() => {
          this.showAddUser = false;
          this.addingUser = false;
          return this.fetchUsers();
        });

    },
    openPasswordDialog(user) {
      this.passwordUser = user;
      this.newPassword = '';
      this.showPassword = true;
    },
    async savePassword() {
      this.savingPassword = true;
      try {
        await api.post('users/password', {
          user: this.passwordUser.id,
          password: this.newPassword
        });
        this.showPassword = false;
        this.newPassword = '';
      } catch (e) {
        console.warn('failed to update password', e);
      } finally {
        this.savingPassword = false;
      }
    },
    async deleteUser(user) {
      if (!window.confirm(`Delete user ${user.email}?`)) return;
      await api.delete(`users/${user.id}`)
        .catch(e => console.warn('failed to delete user', e));
      return this.fetchUsers();
    },
    fetchUsers() {
      return api.get('users/list')
        .then(response => {
          this.users = response.data;
          this.users.forEach(u => {
            // editableRoles drives the multi-select; prefer the new roles[] column,
            // fall back to wrapping the legacy single role for users who haven't been migrated
            u.editableRoles = (u.roles && u.roles.length > 0)
              ? [...u.roles]
              : (u.role !== undefined && u.role !== null ? [u.role] : []);
          });
        })

    }
  }
}
</script>

<style scoped>

</style>
